import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Trash2, Plus, Eye, EyeOff } from "lucide-react";

export function VisibilityToggle({ enabled, onChange, label = "Show this section on the website", testid }) {
  const isOn = enabled !== false;
  return (
    <div
      data-testid={testid}
      className={`flex items-center justify-between gap-4 rounded-xl border px-5 py-4 transition ${
        isOn ? "border-primary/30 bg-primary/5" : "border-amber-400/40 bg-amber-50"
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${isOn ? "bg-primary/15 text-primary" : "bg-amber-200/60 text-amber-800"}`}>
          {isOn ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        </div>
        <div>
          <div className="text-sm font-medium leading-tight">{label}</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {isOn ? "Visible on the public site & in the header navigation." : "Hidden from the public site and header nav until you switch it back on."}
          </div>
        </div>
      </div>
      <label className="relative inline-flex h-7 w-12 items-center cursor-pointer">
        <input
          type="checkbox"
          checked={isOn}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only peer"
        />
        <span className="absolute inset-0 rounded-full bg-border peer-checked:bg-primary transition-colors" />
        <span className="absolute left-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
      </label>
    </div>
  );
}


export function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      <div className="mt-1.5">{children}</div>
      {hint && <span className="mt-1 block text-xs text-muted-foreground/70">{hint}</span>}
    </label>
  );
}

export function TextField({ label, value, onChange, placeholder, type = "text", hint, testid }) {
  return (
    <Field label={label} hint={hint}>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="h-10"
      />
    </Field>
  );
}

export function TextAreaField({ label, value, onChange, placeholder, rows = 3, hint, testid }) {
  return (
    <Field label={label} hint={hint}>
      <Textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        data-testid={testid}
      />
    </Field>
  );
}

export function ListEditor({ title, items, onChange, renderItem, makeBlank, testid }) {
  const update = (idx, next) => {
    const copy = items.slice();
    copy[idx] = next;
    onChange(copy);
  };
  const remove = (idx) => {
    const copy = items.slice();
    copy.splice(idx, 1);
    onChange(copy);
  };
  const add = () => onChange([...(items || []), makeBlank()]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-medium">{title}</h3>
        <Button type="button" onClick={add} variant="outline" size="sm" data-testid={`${testid}-add`}>
          <Plus className="h-4 w-4 mr-1.5" /> Add
        </Button>
      </div>
      {(items || []).map((item, idx) => (
        <div key={idx} data-testid={`${testid}-item-${idx}`} className="relative rounded-lg border border-border bg-secondary/30 p-4 pr-12">
          {renderItem(item, (next) => update(idx, next), idx)}
          <button
            type="button"
            onClick={() => remove(idx)}
            data-testid={`${testid}-remove-${idx}`}
            className="absolute top-3 right-3 h-8 w-8 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 flex items-center justify-center"
            aria-label="Remove"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      {(items || []).length === 0 && (
        <p className="text-sm text-muted-foreground">No items yet. Click <strong>Add</strong> to create one.</p>
      )}
    </div>
  );
}
